import React from 'react';
import { Platform } from 'react-native';
import {
  TransparentModal,
  useNoticeBannersStackContext,
  useResponsive,
  useWebKeyListener,
} from '@sweetgreen/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';
import { useLastInteractedStore } from '@order/LastInteractedStore';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { BagContents } from '../../components/Bag/Bag.contents';
import { useNavigateBack } from '../../navigation';
import type { BagScreenProps } from '../../navigation/AppNavigation.props';

export const BagScreen = ({ navigation }: BagScreenProps) => {
  const isLoggedIn = useIsLoggedIn();
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const lastInteractedStore = useLastInteractedStore();
  const { track } = useTelemetry();

  // ––– HANDLERS ––––––––––––––––––––––––––––––––––––––––––––––––

  // – Navigate back

  const handleNavigateBack = useNavigateBack();
  const navigateBack = React.useCallback(() => {
    track('modal.close');
    handleNavigateBack();
  }, [track, handleNavigateBack]);

  // – Navigate to Checkout (on pressing 'Check out' and successfully applying rewards)

  const navigateToCheckout = React.useCallback(() => {
    if (isLoggedIn) {
      navigation.navigate('Checkout');

      return;
    }

    navigation.navigate('Auth', {
      screen: 'Login',
      params: { redirect: 'checkout' },
    });
  }, [isLoggedIn, navigation]);

  // – On press 'View Menu'

  const navigateToMenu = React.useCallback(() => {
    const { addressId, restaurantSlug } = lastInteractedStore ?? {};

    if (addressId) {
      navigation.navigate('MainTabs', {
        screen: 'MenuTab',
        params: {
          screen: 'DeliveryMenu',
          params: { addressId },
        },
      });
    } else if (restaurantSlug) {
      navigation.navigate('MainTabs', {
        screen: 'MenuTab',
        params: {
          screen: 'Menu',
          params: { restaurantSlug },
        },
      });
    } else {
      navigation.navigate('MainTabs', {
        screen: 'MenuTab',
        params: {
          screen: 'Locations',
        },
      });
    }
  }, [navigation, lastInteractedStore]);

  const dismissAndNavigateToMenu = React.useCallback(() => {
    if (Platform.OS === 'web') {
      // Workaround for a bug in the web implementation that was causing the address bar to display
      // an incorrect URL when navigating from a modal to a screen contained in a stack nested within a tab.
      // It requires us to first dismiss the modal, then navigate after a timeout,
      // otherwise the calls would be debounced.
      navigation.getParent()?.goBack();

      setTimeout(() => {
        navigateToMenu();
      });
    } else {
      navigateToMenu();
    }
  }, [navigation, navigateToMenu]);

  // – Can change location? If signed out, start sign-in and redirect to Locations

  const canChangeLocation = React.useCallback(() => {
    if (isLoggedIn) return true;

    const text = t('bag.reorder.error-logged-out');

    addNoticeBanner({ text, palette: 'neutral' });

    navigation.navigate('Auth', {
      screen: 'Login',
      params: { redirect: 'locations' },
    });
  }, [isLoggedIn, t, addNoticeBanner, navigation]);

  useWebKeyListener('Escape', navigateBack);

  const { match } = useResponsive();

  return (
    <TransparentModal
      size={match(['full', 'drawer'])}
      mobileEdges={['right', 'left']}
      onClose={navigateBack}
    >
      <BagContents
        navigateToMenu={dismissAndNavigateToMenu}
        navigateToCheckout={navigateToCheckout}
        canChangeLocation={canChangeLocation}
      />
    </TransparentModal>
  );
};
